import React, { useState, useCallback, useRef } from 'react';
import { Stamp, Plus, GripVertical, Star, Type, Palette, QrCode, ImagePlus } from 'lucide-react';
import { useStampPresets } from '../../hooks/useStampPresets';
import StampCard from './StampCard';
import AddStampModal from './AddStampModal';
import BgRemovalZone from './BgRemovalZone';
import type { StampPresetItem } from '../../services/api';
import { RunningLoader } from '@/components/RunningLoader';

// 系統預設工具（與 StampToolbar 順序一致；純展示，不可編輯）
const BUILT_IN_TOOLS: { icon: React.FC<{ size?: number; className?: string }>; name: string; desc: string }[] = [
    { icon: Star, name: '一般印章', desc: '愛心、星星、方形、圓形（實心/框線）' },
    { icon: Type, name: '文字印章', desc: '自訂文字、顏色、底色' },
    { icon: Palette, name: '畫筆工具', desc: '畫筆、橡皮擦、滴管' },
    { icon: QrCode, name: 'QR Code', desc: '輸入網址或上傳圖片產生 QR Code' },
    { icon: ImagePlus, name: '上傳圖片印章', desc: '從本機選擇圖片作為印章' },
];

const StampManager: React.FC = () => {
  const { presets, loading, error, toggle, reorder, remove, load } = useStampPresets();
  const [showAddModal, setShowAddModal] = useState(false);

  // ── 簡易拖曳排序 ──────────────────────────────────────────────
  const dragItemRef = useRef<number | null>(null);
  const dragOverRef = useRef<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    dragItemRef.current = index;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragOverRef.current = index;
  }, []);

  const customPresets = presets.filter(p =>
    !p.builtinDataUrl && !['Heart', 'Star', 'QrCode', 'ImagePlus'].includes(p.icon || '')
  );

  const handleDragEnd = useCallback(() => {
    const from = dragItemRef.current;
    const to = dragOverRef.current;
    if (from === null || to === null || from === to) {
      dragItemRef.current = null;
      dragOverRef.current = null;
      return;
    }

    // 只對 customPresets 排序；舊版內建項目順序不影響新版 toolbar
    const items = [...customPresets];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    reorder(items);

    dragItemRef.current = null;
    dragOverRef.current = null;
  }, [customPresets, reorder]);

  // 新增印章後重新載入
  const handleCreated = useCallback((_preset: StampPresetItem) => {
    load();
  }, [load]);

  // 去背工具儲存為印章後重新載入
  const handleBgSaved = useCallback((_preset: StampPresetItem) => {
    load();
  }, [load]);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
      {/* 頁面標題 — 手機隱藏（已由頂部列顯示） */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <Stamp size={18} className="text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">印章管理</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">管理你的印章工具列，自訂快捷印章和排序</p>
        </div>
      </div>

      {/* ── 系統預設工具（純展示） ─────────────────────────────────── */}
      <section className="bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
        <h3 className="font-bold text-sm text-slate-800 dark:text-white mb-1">系統預設工具</h3>
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-3">
          工具列固定提供以下功能，無需設定。
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {BUILT_IN_TOOLS.map(({ icon: Icon, name, desc }) => (
            <div
              key={name}
              className="flex items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 shrink-0">
                <Icon size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-700 dark:text-slate-200">{name}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 自訂印章清單 ─────────────────────────────────────────── */}
      <section className="bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-sm text-slate-800 dark:text-white flex items-center gap-2">
            我的自訂印章
            <span className="text-[10px] font-normal text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
              {customPresets.filter(p => p.enabled).length} / {customPresets.length} 啟用
            </span>
          </h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition-all"
          >
            <Plus size={14} /> 新增印章
          </button>
        </div>

        {/* 拖曳提示 */}
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-3 flex items-center gap-1">
          <GripVertical size={10} /> 拖曳卡片可調整在工具列中的排序
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <RunningLoader />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button onClick={load} className="text-xs text-indigo-500 hover:text-indigo-400">重新載入</button>
          </div>
        ) : customPresets.length === 0 ? (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="w-32 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-3 flex flex-col items-center justify-center gap-2 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-all cursor-pointer"
            >
              <Plus size={20} className="text-slate-400" />
              <span className="text-[10px] text-slate-400 font-medium">新增第一個自訂印章</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {customPresets.map((preset, index) => (
              <div
                key={preset.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={e => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <StampCard
                  preset={preset}
                  onToggle={toggle}
                  onDelete={remove}
                />
              </div>
            ))}

            {/* 新增按鈕卡片 */}
            <button
              onClick={() => setShowAddModal(true)}
              className="w-32 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-3 flex flex-col items-center justify-center gap-2 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-all cursor-pointer"
            >
              <Plus size={20} className="text-slate-400" />
              <span className="text-[10px] text-slate-400 font-medium">新增</span>
            </button>
          </div>
        )}
      </section>

      {/* ── 去背工具 ─────────────────────────────────────────── */}
      <BgRemovalZone onSavedAsPreset={handleBgSaved} />

      {/* ── 新增 Modal ── */}
      {showAddModal && (
        <AddStampModal
          onClose={() => setShowAddModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default StampManager;
